import React, { Dispatch, SetStateAction, useState } from 'react';
import {
  ClickAwayListener,
  Divider,
  makeStyles,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  Typography,
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import { BorderColor } from '@material-ui/icons';
import { SortingCenterUserRole, User } from 'types';
import { colors } from 'theme';
import CollapsableCardWithActions from 'components/Generic/CollapsableCardWithActions';
import ActionButton from 'components/Buttons/ActionButton';
import MoreActionOpenedButtonIcon from 'components/Icons/MoreActionOpenedButtonIcon';
import { DialogSortingCenterUserRolesEdit } from './DialogSortingCenterUserRolesEdit';

const useStyles = makeStyles({
  sortingCenterCode: {
    width: '45%',

    '& span': {
      color: colors.primary,
    },
  },

  sortingCenterRole: {
    width: '45%',
    fontWeight: 400,

    '& span': {
      color: colors.primary,
      fontWeight: 600,
    },
  },

  divider: {
    margin: '10px 8px 10px 11px',
    height: '34px',
  },

  treeBox: {
    width: '100%',
    paddingLeft: '60px',
    paddingBottom: '15px',
    paddingTop: '15px',

    '& > div:first-child > div div:first-child': {
      marginTop: '-15px',
      height: 41,
    },
    '& > div:last-child > div div:last-child': {
      borderLeft: '0px solid transparent',
    },
  },

  sortingCenterLine: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'right',
    height: '52px',
  },

  col1: {
    width: '60px',
    height: '100%',
    '& div': {
      height: '26px',
    },
    '& div:first-child': {
      borderLeft: '1px solid #BFBFBF',
      borderBottom: '1px solid #BFBFBF',
    },
    '& div:last-child': {
      borderLeft: '1px solid #BFBFBF',
    },
  },
  col2: {
    width: '45%',
    paddingLeft: '15px',
  },
  col3: {
    width: '55%',
    '& span span': {
      fontWeight: 400,
    },
  },
  col4: {
    display: 'inline-flex',
    width: '114px',
  },
  divider2: {
    margin: '5px 8px 5px 11px',
  },
  editActionButton2: {
    margin: '6px',
    marginTop: '9px',
    minWidth: '24px',
    '& svg': {
      color: '#2F3741',
      '& path:last-child': {
        fillOpacity: 1,
      },
    },
    '&:hover': {
      backgroundColor: 'transparent',
    },
    '&:hover svg': {
      color: '#0F626F',
    },
    '&:disabled svg': {
      color: '#D9D9D9',
    },
  },
  moreActionButton2: {
    margin: '6px',
    marginTop: '9px',
    width: '0px',
    minWidth: '0px',
    '& svg': {
      fontSize: '1.9rem',
      color: '#2F3741',
    },
    '&:hover': {
      backgroundColor: 'transparent',
    },
    '&:hover svg': {
      color: '#0F626F',
    },
    '&:disabled svg': {
      color: '#D9D9D9',
    },
  },
  mainPaper: {
    color: "'#757575'",
    display: 'flex',
    marginLeft: '-15px',
    paddingRight: '33px',
  },
  menuList: {
    padding: '0 10px',
  },
  menuItem: {
    padding: '8px',
    height: 'auto',
    '&:hover': {
      backgroundColor: 'transparent',
      boxShadow: 'none',
      color: '#1aa1e1',
    },
    '&:not(:last-child)': {
      borderBottom: '1px solid',
      borderBottomColor: '#E0E0E0',
    },
  },
  menuZIndex: {
    zIndex: 1,
  },
});

interface CollapsableUserRoleListContainerProps {
  parentSortingCenterId: number;
  sortingCenterUserRoleList: SortingCenterUserRole[];
  setDialogSortingCenterUserRole?: Dispatch<SetStateAction<SortingCenterUserRole | null>>;
  user: User;
}

const CollapsableUserRoleListContainer: React.FC<CollapsableUserRoleListContainerProps> = ({
  sortingCenterUserRoleList,
  parentSortingCenterId,
  setDialogSortingCenterUserRole = null,
  user,
}) => {
  const classes = useStyles();
  const { t } = useTranslation('translation', { keyPrefix: 'administration.users.show.sortingCenterUserRolesList' });
  const [anchorRef, setAnchorRef] = useState<any>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [selectedSortingCenterUserRole, setSelectedSortingCenterUserRole] = useState<SortingCenterUserRole | null>(
    null,
  );
  const hasMultipleInstances = Boolean(sortingCenterUserRoleList.length > 1);
  const prevAnchorRef = React.useRef(anchorRef);

  const handleClose = (event: React.MouseEvent<EventTarget>) => {
    if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
      return;
    }
    setMenuOpen(false);
  };

  const handleEditClick = (event: PointerEvent, sortingCenterUserRole: SortingCenterUserRole) => {
    setCurrentUserRole(sortingCenterUserRole);
    setIsEditDialog(true);
  };

  const handleClick = (event: PointerEvent, sortingCenterUserRole: SortingCenterUserRole) => {
    setAnchorRef(event.currentTarget);
    setMenuOpen((previousOpen: boolean) => !previousOpen);
    setSelectedSortingCenterUserRole(sortingCenterUserRole);
  };

  const handleClickDialog = () => {
    setMenuOpen(false);
    if (setDialogSortingCenterUserRole) {
      setDialogSortingCenterUserRole(selectedSortingCenterUserRole);
    }
  };

  React.useEffect(() => {
    if (prevAnchorRef.current !== anchorRef) {
      setMenuOpen(true);
    }
    prevAnchorRef.current = anchorRef;
  }, [anchorRef]);

  const ActionsButton = (sortingCenterUserRole: SortingCenterUserRole) => {
    return (
      <>
        <Divider className={classes.divider} orientation={'vertical'} flexItem />
        <ActionButton
          icon={BorderColor}
          data-qa-id={'edit-button'}
          disabled={false}
          className={classes.editActionButton2}
          onClick={(event: PointerEvent) => handleEditClick(event, sortingCenterUserRole)}
        />
        <ActionButton
          icon={MoreVertIcon}
          data-qa-id="action-button"
          disabled={false}
          className={classes.moreActionButton2}
          onClick={(event: PointerEvent) => handleClick(event, sortingCenterUserRole)}
        />
      </>
    );
  };

  const [isEditDialog, setIsEditDialog] = useState(false);
  const [currentUserRole, setCurrentUserRole] = useState<SortingCenterUserRole | null>(null);

  return (
    <>
      <DialogSortingCenterUserRolesEdit
        isOpen={isEditDialog}
        setOpen={setIsEditDialog}
        sortingCenterUserRole={currentUserRole}
        user={user}
      />
      <Popper
        className={classes.menuZIndex}
        anchorEl={anchorRef}
        open={Boolean(menuOpen)}
        role={undefined}
        transition
        placement="right"
        modifiers={{
          preventOverflow: {
            enabled: false,
          },
          hide: {
            enabled: false,
          },
        }}
      >
        <Paper className={classes.mainPaper} elevation={2}>
          <MoreActionOpenedButtonIcon />
          <ClickAwayListener onClickAway={handleClose}>
            <MenuList className={classes.menuList} autoFocusItem={menuOpen} id="menu-list-grow">
              <MenuItem onClick={handleClickDialog} className={classes.menuItem}>
                {t('delete')}
              </MenuItem>
            </MenuList>
          </ClickAwayListener>
        </Paper>
      </Popper>
      {hasMultipleInstances ? (
        <CollapsableCardWithActions
          withActionButtons={true}
          key={parentSortingCenterId}
          disableCollapse={false}
          collapsableContent={
            <div className={classes.treeBox}>
              {sortingCenterUserRoleList
                .sort(function (a: SortingCenterUserRole, b: SortingCenterUserRole) {
                  return a.sortingCenter.code > b.sortingCenter.code ? -1 : 1;
                })
                .map((sortingCenterUserRole, index: number) => (
                  <div key={index} className={classes.sortingCenterLine}>
                    <div className={classes.col1}>
                      <div></div>
                      <div></div>
                    </div>
                    <div className={classes.col2}>
                      <Typography variant="inherit" className={classes.sortingCenterCode}>
                        {sortingCenterUserRole.sortingCenter.name} -{' '}
                        <span>{sortingCenterUserRole.sortingCenter.code}</span>
                      </Typography>
                    </div>
                    <div className={classes.col3}>
                      <Typography variant="inherit" className={classes.sortingCenterRole}>
                        {t('role')} : &nbsp; <span>{t(`userRoles.${sortingCenterUserRole.role}`)}</span>
                      </Typography>
                    </div>
                    <div className={classes.col4}>{ActionsButton(sortingCenterUserRole)}</div>
                  </div>
                ))}
            </div>
          }
        >
          <Typography variant="inherit" className={classes.sortingCenterCode}>
            {sortingCenterUserRoleList[0].sortingCenter.parentSortingCenter?.name} -{' '}
            <span>{sortingCenterUserRoleList[0].sortingCenter.parentSortingCenter?.code}</span>
          </Typography>
          <Typography variant="inherit" className={classes.sortingCenterRole}>
            {t('role')} : &nbsp; <span>-</span>
          </Typography>
        </CollapsableCardWithActions>
      ) : (
        sortingCenterUserRoleList.map((sortingCenterUserRole: SortingCenterUserRole, index: number) => (
          <CollapsableCardWithActions
            key={index}
            withActionButtons={true}
            disableCollapse={true}
            collapsableContent={''}
            customActionButtons={ActionsButton(sortingCenterUserRole)}
          >
            <Typography variant="inherit" className={classes.sortingCenterCode}>
              {sortingCenterUserRole.sortingCenter.name} - <span>{sortingCenterUserRole.sortingCenter.code}</span>
            </Typography>
            <Typography variant="inherit" className={classes.sortingCenterRole}>
              {t('role')} : &nbsp; <span>{t(`userRoles.${sortingCenterUserRole.role}`)}</span>
            </Typography>
          </CollapsableCardWithActions>
        ))
      )}
    </>
  );
};

export default CollapsableUserRoleListContainer;
